import { useEffect, useRef, type ReactNode } from "react";
import { useGetWellbeingSettings, getGetWellbeingSettingsQueryKey, type WellbeingSettings } from "@workspace/api-client-react";
import { toast } from "@/hooks/use-toast";

function todayKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

type ActivityRecord = { firstActivityAt: number; lastActivityAt: number };

function loadActivity(date: string): ActivityRecord | null {
  try {
    const saved = localStorage.getItem(`assistio.wellbeing.activity.${date}`);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

let lastActivityWriteAt = 0;

// Throttled — click/keydown fire far more often than the minute-level
// resolution this is ever read at needs, so most events are no-ops here.
function recordActivity(date: string) {
  const now = Date.now();
  if (now - lastActivityWriteAt < 10_000) return;
  lastActivityWriteAt = now;

  const existing = loadActivity(date);
  const next: ActivityRecord = { firstActivityAt: existing?.firstActivityAt ?? now, lastActivityAt: now };
  try {
    localStorage.setItem(`assistio.wellbeing.activity.${date}`, JSON.stringify(next));
  } catch {
    // localStorage unavailable — activity just won't be tracked
  }
}

// One JSON array of "already nudged today" keys (e.g. "break:12:30", "early",
// "late", "longDay") per day, rather than a separate localStorage key per
// nudge — keeps each nudge type firing at most once per day regardless of
// how many 60s ticks land after its condition becomes true.
function hasFired(date: string, key: string): boolean {
  try {
    const saved = localStorage.getItem(`assistio.wellbeing.fired.${date}`);
    const fired: string[] = saved ? JSON.parse(saved) : [];
    return fired.includes(key);
  } catch {
    return false;
  }
}

function markFired(date: string, key: string) {
  try {
    const storageKey = `assistio.wellbeing.fired.${date}`;
    const saved = localStorage.getItem(storageKey);
    const fired: string[] = saved ? JSON.parse(saved) : [];
    if (!fired.includes(key)) localStorage.setItem(storageKey, JSON.stringify([...fired, key]));
  } catch {
    // localStorage unavailable — this nudge type may fire again this session
  }
}

function runTick(settings: WellbeingSettings) {
  const now = new Date();
  const date = todayKey(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const activity = loadActivity(date);
  const activeRecently = activity !== null && Date.now() - activity.lastActivityAt < 5 * 60_000;

  if (settings.breakRemindersEnabled) {
    const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    if (settings.breakTimes.includes(hhmm) && !hasFired(date, `break:${hhmm}`)) {
      markFired(date, `break:${hhmm}`);
      toast({ title: "Time for a break", description: "Stretch, grab some water, or step outside for a minute." });
    }
  }

  if (settings.workingHoursNudgesEnabled && activeRecently) {
    const [startHour, endHour] = settings.workHoursByDay[now.getDay()] ?? [9, 17];
    const startMinutes = startHour * 60;
    const endMinutes = endHour * 60;

    if (nowMinutes < startMinutes - 60 && !hasFired(date, "early")) {
      markFired(date, "early");
      toast({
        title: "You're at it early",
        description: "Before you dive in — what's the one thing that actually needs to happen this morning?",
      });
    }

    if (nowMinutes > endMinutes + 60 && !hasFired(date, "late")) {
      markFired(date, "late");
      const lateNight = now.getHours() >= 21 || now.getHours() < 5;
      toast({
        title: "Still going?",
        description: lateNight
          ? "Anything you send now will likely sit until morning anyway — worth wrapping up and coming back fresh?"
          : "You're well past your usual finish time — let's figure out what's actually left before you keep going.",
      });
    }

    if (activity && !hasFired(date, "longDay")) {
      const hoursActive = (activity.lastActivityAt - activity.firstActivityAt) / 3_600_000;
      if (hoursActive >= settings.longDayThresholdHours) {
        markFired(date, "longDay");
        toast({
          title: "That's a long day",
          description: `You've been at this for about ${Math.round(hoursActive)} hours today — worth a proper break?`,
        });
      }
    }
  }
}

export function WellbeingEngineProvider({ children }: { children: ReactNode }) {
  const { data: settings } = useGetWellbeingSettings({ query: { queryKey: getGetWellbeingSettingsQueryKey() } });
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  useEffect(() => {
    const onActivity = () => recordActivity(todayKey());
    onActivity();
    document.addEventListener("click", onActivity);
    document.addEventListener("keydown", onActivity);
    document.addEventListener("visibilitychange", onActivity);
    return () => {
      document.removeEventListener("click", onActivity);
      document.removeEventListener("keydown", onActivity);
      document.removeEventListener("visibilitychange", onActivity);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (settingsRef.current) runTick(settingsRef.current);
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  return <>{children}</>;
}
